import { useEffect, useState } from 'react';
import { Plus, Handshake, MoreHorizontal, Check, X, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  StatusBadge,
  ConfirmDialog,
} from '@/components/common';
import { partnerNetworksApi, PartnerNetwork } from '@/api/services';
import { getErrorMessage } from '@/api/client';

export default function PartnerNetworks() {
  const [asOwner, setAsOwner] = useState<PartnerNetwork[]>([]);
  const [asPartner, setAsPartner] = useState<PartnerNetwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [terminateDialog, setTerminateDialog] = useState<{
    open: boolean;
    partnership?: PartnerNetwork;
  }>({ open: false });
  const [terminating, setTerminating] = useState(false);

  const fetchPartnerships = async () => {
    try {
      setLoading(true);
      const data = await partnerNetworksApi.getAll();
      setAsOwner(data.asOwner);
      setAsPartner(data.asPartner);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartnerships();
  }, []);

  const handleAccept = async (partnership: PartnerNetwork) => {
    try {
      await partnerNetworksApi.accept(partnership.id);
      toast.success('Partnership accepted');
      fetchPartnerships();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleTerminate = async () => {
    if (!terminateDialog.partnership) return;

    try {
      setTerminating(true);
      await partnerNetworksApi.delete(terminateDialog.partnership.id);
      toast.success('Partnership terminated');
      setTerminateDialog({ open: false });
      fetchPartnerships();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setTerminating(false);
    }
  };

  const ownerColumns: Column<PartnerNetwork>[] = [
    {
      key: 'partnerCompany',
      header: 'Partner',
      render: (p) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10">
            <Handshake className="h-4 w-4 text-accent" />
          </div>
          <div>
            <div className="font-medium">{p.partnerCompany?.displayName}</div>
            <div className="text-sm text-muted-foreground">
              {p.partnerCompany?.name}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'partnerTypes',
      header: 'Types',
      render: (p) => (
        <div className="flex flex-wrap gap-1">
          {p.partnerTypes.slice(0, 2).map((type) => (
            <Badge key={type} variant="outline" className="text-xs">
              {type}
            </Badge>
          ))}
          {p.partnerTypes.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{p.partnerTypes.length - 2}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (p) => <StatusBadge status={p.status.toLowerCase() as any} />,
    },
    {
      key: 'accessLevel',
      header: 'Access',
      render: (p) => (
        <Badge variant="secondary" className="text-xs">
          {p.accessLevel.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      key: 'relationshipStartDate',
      header: 'Since',
      render: (p) =>
        p.relationshipStartDate
          ? new Date(p.relationshipStartDate).toLocaleDateString()
          : '-',
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (p) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            {p.status !== 'TERMINATED' && (
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => setTerminateDialog({ open: true, partnership: p })}
              >
                <X className="mr-2 h-4 w-4" />
                Terminate
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const partnerColumns: Column<PartnerNetwork>[] = [
    {
      key: 'ownerCompany',
      header: 'Company',
      render: (p) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <Handshake className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="font-medium">{p.ownerCompany?.displayName}</div>
            <div className="text-sm text-muted-foreground">
              {p.ownerCompany?.name}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'partnerTypes',
      header: 'Your Role',
      render: (p) => (
        <div className="flex flex-wrap gap-1">
          {p.partnerTypes.map((type) => (
            <Badge key={type} variant="outline" className="text-xs">
              {type}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (p) => <StatusBadge status={p.status.toLowerCase() as any} />,
    },
    {
      key: 'accessLevel',
      header: 'Access',
      render: (p) => (
        <Badge variant="secondary" className="text-xs">
          {p.accessLevel.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (p) =>
        p.status === 'PENDING' ? (
          <div className="flex gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-emerald-600"
              onClick={() => handleAccept(p)}
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-destructive"
              onClick={() => setTerminateDialog({ open: true, partnership: p })}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>
                <Eye className="mr-2 h-4 w-4" />
                View Details
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
    },
  ];

  return (
    <div className="p-6">
      <PageHeader
        title="Partner Networks"
        description="Manage business partnerships and collaborations"
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Partner Networks' },
        ]}
        actions={
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add Partner
          </Button>
        }
      />

      <Tabs defaultValue="my-partners" className="w-full">
        <TabsList>
          <TabsTrigger value="my-partners">
            My Partners ({asOwner.length})
          </TabsTrigger>
          <TabsTrigger value="partner-of">
            Partner Of ({asPartner.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="my-partners" className="mt-4">
          <DataTable
            data={asOwner}
            columns={ownerColumns}
            loading={loading}
            emptyTitle="No partners yet"
            emptyDescription="Invite business partners to collaborate."
            emptyAction={
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Partner
              </Button>
            }
          />
        </TabsContent>

        <TabsContent value="partner-of" className="mt-4">
          <DataTable
            data={asPartner}
            columns={partnerColumns}
            loading={loading}
            emptyTitle="Not a partner of any company"
            emptyDescription="You'll see partnership invitations here."
          />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={terminateDialog.open}
        onOpenChange={(open) => setTerminateDialog({ open })}
        title="Terminate Partnership"
        description="Are you sure you want to terminate this partnership? The partner will lose access to shared resources."
        confirmLabel="Terminate"
        onConfirm={handleTerminate}
        variant="destructive"
        loading={terminating}
      />
    </div>
  );
}
